import { useCallback, useEffect, useState } from 'react';
import { buildStatusRows, resolveInstallSetting, resolveNotificationSetting } from '../../domain/pwa/appSettings';
import { NOTIFICATION_STATUS } from '../../domain/pwa/notificationOffer';
import { INSTALL_SUPPORT } from '../../domain/pwa/installOffer';
import { translate } from '../../domain/i18n/messages';
import {
    getPlantNotificationCapability,
    requestPlantNotificationPermission,
    showAppNotification,
} from '../notifications/plantNotificationCapability';
import {
    ARRIVAL_SYNC_TAG,
    checkForPlantArrivals,
    ensureBackgroundArrivalSync,
} from '../notifications/plantArrivalWatch';
import { mirrorPlantWatchState, readLastCheckedAt, readSeenPlants } from '../notifications/plantArrivalStore';
import { currentRuntimeVersion } from './appUpdateStatus';
import { promptForInstall, subscribeInstallPromptState } from './installPrompt';
import { readNotificationsMuted, writeNotificationsMuted } from './offerPreferences';

/**
 * Everything the settings drawer shows, and the four things it can change
 * (SPEC-018).
 *
 * Reads on every open rather than once on mount. The drawer is the surface a
 * visitor goes to precisely BECAUSE they suspect something has changed — they
 * granted permission in a system dialog, installed from the browser's own menu,
 * came back a week later — and a panel that opens showing what was true when the
 * app started is worse than no panel, because it looks authoritative.
 */

/**
 * Whether the browser has actually accepted a background check.
 *
 * Asked rather than assumed: `ensureBackgroundArrivalSync` returns false for
 * four different reasons and Chrome may also drop a registration later. The
 * status row has to report what IS, not what was requested.
 */
async function readBackgroundSyncState(navigatorObject) {
    if (!navigatorObject || !('serviceWorker' in navigatorObject)) {
        return { hasServiceWorker: false, hasBackgroundSync: false };
    }

    try {
        const registration = await navigatorObject.serviceWorker.getRegistration();

        if (!registration) {
            return { hasServiceWorker: false, hasBackgroundSync: false };
        }

        if (!registration.periodicSync || typeof registration.periodicSync.getTags !== 'function') {
            return { hasServiceWorker: true, hasBackgroundSync: false };
        }

        const tags = await registration.periodicSync.getTags();

        return { hasServiceWorker: true, hasBackgroundSync: Array.isArray(tags) && tags.includes(ARRIVAL_SYNC_TAG) };
    } catch (error) {
        return { hasServiceWorker: Boolean(navigatorObject.serviceWorker.controller), hasBackgroundSync: false };
    }
}

export function useAppSettings({ isOpen = false, localeCode = '', languageCode = 'en' } = {}) {
    const [promptState, setPromptState] = useState({ support: INSTALL_SUPPORT.unsupported, isStandalone: false });
    const [notificationStatus, setNotificationStatus] = useState(() => getPlantNotificationCapability().status);
    const [isMuted, setIsMuted] = useState(readNotificationsMuted);
    const [device, setDevice] = useState({ hasServiceWorker: false, hasBackgroundSync: false });
    const [watch, setWatch] = useState({ knownPlantCount: 0, lastCheckedAt: null });
    const [isBusy, setIsBusy] = useState(false);

    useEffect(() => subscribeInstallPromptState(setPromptState), []);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        let isActive = true;

        setNotificationStatus(getPlantNotificationCapability().status);
        setIsMuted(readNotificationsMuted());
        setWatch({ knownPlantCount: readSeenPlants().plantIds.length, lastCheckedAt: readLastCheckedAt() });

        readBackgroundSyncState(typeof navigator === 'undefined' ? null : navigator).then((next) => {
            if (isActive) {
                setDevice(next);
            }
        });

        return () => {
            isActive = false;
        };
    }, [isOpen]);

    const notification = resolveNotificationSetting({
        status: notificationStatus,
        isMuted,
        installSupport: promptState.support,
    });
    const install = resolveInstallSetting({ installSupport: promptState.support });

    /**
     * Ask the browser, once.
     *
     * The same confirmation SPEC-016 D-11 sends from the offer card, for the same
     * reason: the next real notification may be a deploy away, and a permission
     * that silently failed looks exactly like one that worked.
     */
    const turnOnNotifications = useCallback(async () => {
        setIsBusy(true);

        try {
            const capability = await requestPlantNotificationPermission();

            setNotificationStatus(capability.status);

            if (capability.status !== NOTIFICATION_STATUS.granted) {
                return capability.status;
            }

            // Un-mute, because arriving here means the visitor just asked for
            // notifications: leaving an old mute in place would grant permission
            // and then honour a decision they have plainly reversed.
            writeNotificationsMuted(false);
            setIsMuted(false);

            await checkForPlantArrivals({ localeCode, languageCode });
            setDevice(await readBackgroundSyncState(typeof navigator === 'undefined' ? null : navigator));
            await ensureBackgroundArrivalSync();
            await showAppNotification({
                title: translate(languageCode, 'pwa.notify.confirmed.title'),
                body: translate(languageCode, 'pwa.notify.confirmed.body'),
                tag: 'plant-notifications-on',
                url: localeCode ? `/${localeCode}/plants` : '/',
            });

            return capability.status;
        } finally {
            setIsBusy(false);
        }
    }, [languageCode, localeCode]);

    /** Stored AND mirrored, because the worker reads only the mirror. */
    const setMuted = useCallback(async (nextMuted) => {
        writeNotificationsMuted(nextMuted);
        setIsMuted(nextMuted);

        await mirrorPlantWatchState({
            localeCode,
            seenPlantIds: readSeenPlants().plantIds,
            isMuted: nextMuted,
            lastCheckedAt: readLastCheckedAt(),
        });
    }, [localeCode]);

    const installApp = useCallback(() => promptForInstall(), []);

    return {
        notification,
        install,
        isBusy,
        statusRows: buildStatusRows({
            isStandalone: promptState.isStandalone,
            hasServiceWorker: device.hasServiceWorker,
            hasBackgroundSync: device.hasBackgroundSync,
            knownPlantCount: watch.knownPlantCount,
            lastCheckedAt: watch.lastCheckedAt,
            appVersion: currentRuntimeVersion.appVersion,
        }),
        turnOnNotifications,
        setMuted,
        installApp,
    };
}
